import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { mvpApi } from '../services/api';

const STORAGE_KEY = 'advisor_active_project_id';

const AdvisorProjectContext = createContext(null);

export function AdvisorProjectProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeProjectId, setActiveProjectIdState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      const parsed = Number(stored);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  });

  const loadProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await mvpApi.listAdminProjects();
      const list = res.data?.projects || [];
      setProjects(list);
      setActiveProjectIdState((prev) => {
        if (prev && list.some((p) => Number(p.id) === Number(prev))) {
          return prev;
        }
        const newest = [...list].sort((a, b) => Number(b.id) - Number(a.id))[0];
        const firstId = newest?.id ?? list[0]?.id ?? null;
        if (firstId != null) {
          try {
            localStorage.setItem(STORAGE_KEY, String(firstId));
          } catch {
            /* ignore */
          }
        }
        return firstId;
      });
    } catch {
      setError('load_failed');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const setActiveProjectId = useCallback((id) => {
    setActiveProjectIdState(id);
    try {
      if (id != null) localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      /* ignore */
    }
  }, []);

  const activeProject = useMemo(
    () => projects.find((p) => Number(p.id) === Number(activeProjectId)) || null,
    [projects, activeProjectId],
  );

  const value = useMemo(
    () => ({
      projects,
      loading,
      error,
      activeProjectId,
      activeProject,
      setActiveProjectId,
      reloadProjects: loadProjects,
    }),
    [projects, loading, error, activeProjectId, activeProject, setActiveProjectId, loadProjects],
  );

  return (
    <AdvisorProjectContext.Provider value={value}>
      {children}
    </AdvisorProjectContext.Provider>
  );
}

export function useAdvisorProject() {
  const ctx = useContext(AdvisorProjectContext);
  if (!ctx) {
    throw new Error('useAdvisorProject must be used within AdvisorProjectProvider');
  }
  return ctx;
}
